import React from "react";
import { Text } from "@vibe/core";
import { explainFailure, joinNames } from "./resyncCopy";

export interface ResyncFailureLine {
  boardName: string;
  /** The column that was refused, when the refusal was per-column rather than per-board. */
  columnTitle?: string;
  reason: string;
}

interface ResyncOutcomeProps {
  /** What was synced, in the user's words: a column title, or a subitem's name for "Sync all". */
  subject: string;
  /** Board names the write landed on. */
  updatedBoards: string[];
  notUpdated: ResyncFailureLine[];
  /** Whole requests that were rejected outright, one sentence each. */
  rejected?: string[];
}

const NOTICE_STYLE: React.CSSProperties = {
  padding: "var(--spacing-small) var(--spacing-medium)",
  borderRadius: "var(--border-radius-small)",
};

/**
 * What a single-column (or single-subitem) resync actually did, after the fact.
 *
 * Shared by both tabs' dialogs, and shaped like the bulk write report: the outcome first, then
 * every board that was not updated with the reason in plain words. "Sent to:" and "Not sent:"
 * described the request; this describes the boards.
 *
 * A result that reports nothing at all — no board updated, none refused, nothing rejected — is
 * the benign "already in sync" case (a stale row, a double click, a teammate syncing first) and
 * says so, rather than rendering an empty panel under a Done button.
 */
const ResyncOutcome = ({ subject, updatedBoards, notUpdated, rejected = [] }: ResyncOutcomeProps) => {
  const nothingHappened = updatedBoards.length === 0 && notUpdated.length === 0 && rejected.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
      {nothingHappened ? (
        <Text type="text1" element="p">
          Nothing to change. These boards already agree.
        </Text>
      ) : null}

      {updatedBoards.length > 0 ? (
        <div style={{ ...NOTICE_STYLE, background: "var(--positive-color-selected)" }}>
          <Text type="text1" element="p">
            <strong>{subject}</strong>
            {` was updated on ${joinNames(updatedBoards)}.`}
          </Text>
        </div>
      ) : null}

      {notUpdated.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
          <Text type="text1" weight="medium" color="negative" element="p">
            Not updated:
          </Text>
          <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
            {notUpdated.map((failure, index) => {
              const explained = explainFailure(failure.reason);
              return (
                <li key={`${failure.boardName}-${failure.columnTitle ?? "board"}-${index}`}>
                  <Text type="text2" weight="medium" element="div">
                    {failure.columnTitle ? `${failure.boardName} — ${failure.columnTitle}` : failure.boardName}
                  </Text>
                  {/* The engine's reason, in plain words where we have them and verbatim where we
                      do not — never collapsed into a generic message. This is what surfaces a
                      platform-limit refusal as the limit it is, not a bug. */}
                  <Text type="text2" color="secondary" element="div">
                    {explained.message}
                  </Text>
                  {explained.nextStep ? (
                    <Text type="text2" element="div">
                      {explained.nextStep}
                    </Text>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {rejected.map((sentence) => (
        <Text key={sentence} type="text2" color="negative" element="p">
          {sentence}
        </Text>
      ))}
    </div>
  );
};

export default ResyncOutcome;
